import React, { useEffect, useRef, useState } from "react";
import { variantSrcSet, dropVariants } from "../lib/imgVariants";
// Prerendered HTML already shows the hero text; replaying the entrance fade on hydration
// hides it again for ~1.5 s and pushes mobile LCP out. Skip the entrance only in that case.
const PRERENDERED = typeof document !== "undefined" && document.documentElement.hasAttribute("data-prerendered");
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { api } from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import HomeSearch from "../components/HomeSearch";
import PropertyRail from "../components/PropertyRail";
import HeroVideo from "../components/HeroVideo";
import { SEARCH_AREAS } from "../lib/searchAreas";
import { readiness, isReadyish } from "../lib/readiness";
import { formatAED } from "../lib/api";
import SEO from "../components/SEO";
import ExploreLinks from "../components/ExploreLinks";
import BuyerAnswers from "../components/BuyerAnswers";
import {
  Marquee, CountUp, RevealHeading, MagneticButton, SectionNumber, PullQuote,
} from "../components/polish";

// Rotating hero — official developer renders only, self-hosted.
// Each slide carries its own accent hue so the typography matches the sky.
const HERO_SLIDES = [
  {
    img: "/images/bay-views/img6.webp",
    alt: "Bay Views, Hayat Island — sunset over the Arabian Gulf",
    accent: "#e0ae62", // sunset gold
  },
  {
    img: "/images/bay-views/img4.webp",
    alt: "Bay Views, Hayat Island — sunrise over the Arabian Gulf",
    accent: "#a7c9d4", // morning sea
  },
];
const HERO_INTERVAL_MS = 5000;
const HERO_VIDEO = "bayviews-sunset"; // public/videos/hero/<name>-{720,1080}.{webm,mp4}
const PHILOSOPHY_IMG = "/images/bay-views/philosophy.webp"; // self-hosted (was hot-linked from rakproperties.ae, 318 KB)
// 1080/1920-px variants sit next to each hero render; phones get ~70 KB instead of 380 KB.
const heroSrcSet = (img) => { const i = img.replace(/^\/images\//, "/i/"); return `${i.replace(".webp", "-1080.webp")} 1080w, ${i.replace(".webp", "-1920.webp")} 1920w, ${img} 2560w`; };

const MARQUEE_ITEMS = [
  "RERA 41793",
  "1 Billion Dirhams+ Portfolio",
  "24 / 7 Sofia Concierge",
  "Dubai · Ras Al Khaimah",
  "1,500+ Off-Plan Projects",
  "Golden Visa Eligible",
];

const STATS = [
  { to: 1.0,    suffix: "B Dirhams",   label: "Portfolio value",   decimals: 1 },
  { to: 74,     suffix: "",            label: "Own listings",      decimals: 0 },
  { to: 24,     suffix: "/ 7",         label: "Sofia concierge",   decimals: 0, slash: true },
  { to: 116,    suffix: "M Dirhams",   label: "Ras Al Khaimah beachfront",    decimals: 0 },
];

// Helper to check if an image URL is a real property image (not a placeholder)
const isRealPropertyImage = (url) => {
  if (!url) return false;
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.includes('unsplash.com') || lowerUrl.includes('pexels.com')) return false;
  if (lowerUrl.includes('status@broadcast') || lowerUrl.includes('status%40broadcast')) return false;
  // Self-hosted sanitized images (DB rewrote hotlinks to local paths in June 2026)
  if (lowerUrl.startsWith('/images/')) return true;
  return lowerUrl.includes('sofiasandsrealty.ae') ||
         lowerUrl.includes('reelly.io') ||
         lowerUrl.includes('rakproperties.ae') ||
         lowerUrl.includes('emaar.com') ||
         lowerUrl.includes('meraas.ae') ||
         lowerUrl.includes('nakheel.com') ||
         lowerUrl.includes('sobharealty.com') ||
         lowerUrl.includes('damacproperties.com') ||
         lowerUrl.includes('azizidevelopments.com');
};

export default function Home() {
  const [posts, setPosts] = useState([]);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [slide, setSlide] = useState(0);
  const heroRef = useRef(null);

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 180]);
  const heroScale = useTransform(scrollY, [0, 800], [1, 1.08]);

  // Auto-rotate hero imagery; typography accent follows the sky
  useEffect(() => {
    const t = null; // hero is now one cinematic video loop (31 Oct 2026) — no slide rotation
    return () => clearInterval(t);
  }, []);
  const accent = HERO_SLIDES[slide].accent;
  const accentStyle = { color: accent, transition: "color 1.6s ease" };

  useEffect(() => {
    api.get("/blog").then((r) => {
      // 3 distinct posts: dedupe by title and image, strip any raw markdown from excerpts
      const seenTitles = new Set();
      const seenImages = new Set();
      const distinct = (r.data.items || []).filter((p) => {
        if (seenTitles.has(p.title) || seenImages.has(p.hero_image)) return false;
        seenTitles.add(p.title);
        seenImages.add(p.hero_image);
        return true;
      }).slice(0, 3).map((p) => ({
        ...p,
        excerpt: (p.excerpt || "")
          .split("\n").filter((l) => !/^\s*#/.test(l)).join(" ")
          .replace(/\*\*|__|[*_`>]|<[^>]+>/g, "")
          .replace(/\s+/g, " ").trim(),
      }));
      setPosts(distinct);
    }).catch(() => setPosts([]));

    // Fetch listings and filter for real property images only
    api.get("/properties", { params: { limit: 200 } }).then((r) => {
      const allListings = r.data.items || [];
      // Filter to only listings with real property images
      const withRealImages = allListings.filter(p => isRealPropertyImage(p.hero_image));

      // Sort: Bay Views first, then other featured listings, then by price descending
      const sorted = withRealImages.sort((a, b) => {
        // Bay Views always first
        if (a.slug === 'bay-views-hayat-island-rak') return -1;
        if (b.slug === 'bay-views-hayat-island-rak') return 1;
        // Then featured listings
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;
        // Then by price descending
        return (b.price || 0) - (a.price || 0);
      });

      // Take top 6 for homepage
      setFeaturedListings(sorted);
    }).catch(() => setFeaturedListings([]));
  }, []);

  const openSofia = () => document.querySelector('[data-testid="sofia-chat-fab"]')?.click();

  return (
    <div data-testid="home-page">
      <SEO
        title="Luxury Real Estate Dubai & RAK"
        description="RERA 41793 brokerage for Dubai & Ras Al Khaimah. Bay Views, Hayat Island: one full floor, ready now, AED 16.2M. Sofia answers 24/7 on WhatsApp."
        keywords="luxury real estate Dubai, RAK property, Hayat Island, Bay Views, Golden Visa UAE, Dubai property investment, Ras Al Khaimah real estate, RERA 41793"
        url="/"
      />
      {/* ============ HERO — image slider + portal search (31 Oct 2026) ============ */}
      <section ref={heroRef} className="relative min-h-[88vh] flex items-end overflow-hidden bg-ink" data-testid="home-hero">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0 w-full h-full">
          <HeroVideo name={HERO_VIDEO}>
            <img
              src={HERO_SLIDES[0].img}
              srcSet={heroSrcSet(HERO_SLIDES[0].img)}
              sizes="(max-width: 820px) 70vw, 100vw"
              width={2560}
              height={1184}
              alt={HERO_SLIDES[0].alt}
              className="absolute inset-0 w-full h-full object-cover"
              fetchpriority="high"
              loading="eager"
              decoding="async"
            />
          </HeroVideo>
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/50 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-[65%] bg-gradient-to-t from-ink via-ink/60 to-transparent" />
        <div className="vignette" aria-hidden="true" />

        <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pb-16 md:pb-20 pt-36 text-bone">
          <div className="flex items-center gap-4 mb-8">
            <span className="stamp stamp-rera" data-testid="hero-rera-stamp">RERA 41793</span>
            <span className="h-px w-14" style={{ backgroundColor: accent, opacity: 0.6, transition: "background-color 1.6s ease" }} />
            <h1 className="overline" style={accentStyle}>Property for sale · Dubai · Ras Al Khaimah</h1>
          </div>
          <p className="font-display text-[2.6rem] md:text-[4.2rem] leading-[1.02] tracking-tightest text-bone mb-10">
            Residences <em className="italic" style={accentStyle}>selected</em>, never listed.
          </p>
          <HomeSearch />
          <div className="absolute bottom-4 left-6 right-6 md:left-auto md:bottom-6 md:right-12 lg:right-20 text-right text-[0.55rem] md:text-[0.58rem] tracking-[0.14em] uppercase text-bone/45" data-testid="hero-video-caption">
            Bay Views, Hayat Island · animated from the official developer render
          </div>
          <button type="button" onClick={openSofia} className="mt-5 text-bone/70 hover:text-gold text-sm underline underline-offset-4" data-testid="hero-cta-sofia">
            Not sure where to start? Ask Sofia
          </button>
        </div>
      </section>

      {/* ============ READY TO MOVE IN — swipeable slider (badge shows confirmed "Ready" or the stated handover date) ============ */}
      <section className="px-6 md:px-12 lg:px-20 pt-24 pb-12" data-testid="featured-section">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <div className="overline overline-gold mb-3">Our residences</div>
            <h2 className="font-display text-h2 text-ink leading-tight">Ready to move in</h2>
          </div>
          <Link to="/search?type=ready" className="hidden sm:inline-flex items-center gap-2 text-[0.7rem] tracking-[0.16em] uppercase text-ink/70 hover:text-gold md:mr-28" data-testid="featured-view-all">View all <ArrowUpRight size={13} /></Link>
        </div>
        <PropertyRail items={featuredListings.filter((p) => readiness(p) === "Ready").slice(0, 12)} testid="rail-ready" />
      </section>

      {/* ============ OFF-PLAN — swipeable slider ============ */}
      <section className="px-6 md:px-12 lg:px-20 pt-12 pb-24" data-testid="offplan-rail-section">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <div className="overline overline-gold mb-3">Launches</div>
            <h2 className="font-display text-h2 text-ink leading-tight">Off-plan &amp; new launches</h2>
          </div>
          <Link to="/search?type=offplan" className="hidden sm:inline-flex items-center gap-2 text-[0.7rem] tracking-[0.16em] uppercase text-ink/70 hover:text-gold md:mr-28">View all <ArrowUpRight size={13} /></Link>
        </div>
        <PropertyRail items={featuredListings.filter((p) => readiness(p) === "Off-Plan").slice(0, 12)} testid="rail-offplan" />
      </section>

      {/* ============ EXPLORE BY AREA — image tiles → area pages (crawlable links) ============ */}
      <section className="px-6 md:px-12 lg:px-20 py-24 bg-sand" data-testid="areas-section">
        <div className="overline overline-gold mb-3">Areas</div>
        <h2 className="font-display text-h2 text-ink leading-tight mb-10">Explore by area</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {SEARCH_AREAS.filter((a) => a.forSale && a.img).slice(0, 8).map((a) => (
            <a key={a.id} href={a.forSale} className="group relative aspect-[4/5] md:aspect-[3/4] overflow-hidden bg-ink block" data-testid={`area-tile-${a.id}`}>
              <img src={a.img} srcSet={variantSrcSet(a.img)} sizes="(max-width: 768px) 50vw, 25vw" alt={`${a.label} — property for sale`} width={600} height={800} loading="lazy" decoding="async"
                onError={(e) => dropVariants(e.currentTarget)} className="absolute inset-0 w-full h-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-[1.05]" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                <div className="font-display text-bone text-[1.25rem] md:text-[1.5rem] leading-tight">{a.label}</div>
                <div className="text-bone/70 text-[0.62rem] tracking-[0.14em] uppercase mt-1 group-hover:text-gold">Property for sale →</div>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* ============ ABOUT + SOFIA — one compact band (factual lede stays crawlable) ============ */}
      <section className="px-6 md:px-12 lg:px-20 py-24 bg-ink text-bone" data-testid="sofia-cta-section">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="overline overline-gold mb-4">Sofia Sands Realty · RERA 41793</div>
            <h2 className="font-display text-h2 leading-tight">To <em className="italic text-gold">Dubai-it</em> — excellence, in record time.</h2>
            {/* Direct answer — plain, crawlable text (moved out of the hero 31 Oct 2026; AI engines quote it).
                Source: seo_recovery/content/geo/homepage_lede.html (65 words). */}
            <p
              className="mt-6 max-w-3xl text-bone/65 text-[0.95rem] leading-relaxed font-light"
              data-testid="home-lede"
            >
              Sofia Sands Realty LLC is a licensed UAE real estate brokerage serving Dubai and
              Ras Al Khaimah, under RERA broker registration 41793. The firm sells 12 handed-over,
              ready Bay Views residences on Hayat Island, sold together as one full floor at
              AED 16,200,000. Other stock includes two ready one-bedrooms at Bay Residences and a
              Marina Shores two-bedroom in Dubai Marina at AED 3,450,000. The principal broker is
              Yitayal Mesfin.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <button type="button" onClick={openSofia} className="inline-flex items-center justify-center gap-3 bg-bone text-ink hover:bg-gold px-8 py-4 text-[0.72rem] tracking-[0.16em] uppercase font-medium transition-colors">Talk to Sofia <ArrowUpRight size={16} /></button>
            <a href="https://wa.me/971547054704?text=Hi%20Sofia%2C%20I%27m%20looking%20at%20property%20in%20Dubai%20%2F%20RAK.%20What%20do%20you%20have%20that%27s%20ready%20now%20in%20my%20range%3F" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 border border-bone/40 hover:border-gold hover:text-gold px-8 py-4 text-[0.72rem] tracking-[0.16em] uppercase font-medium transition-colors">WhatsApp us</a>
          </div>
        </div>
      </section>

      {/* ============ BUYER QUESTIONS (FAQ, crawlable) ============ */}
      <BuyerAnswers />

      {/* ============ INSIGHTS PREVIEW ============ */}
      <section className="px-6 md:px-12 lg:px-20 py-32 md:py-section-md bg-sand" data-testid="insights-preview">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-8 mb-16">
          <div>
            <div className="mb-6">
              <SectionNumber n={4} label="Research" />
            </div>
            <RevealHeading
              as="h2"
              stagger={0.04}
              className="font-display text-h1 leading-tight tracking-tightest text-ink"
            >
              The latest from <em className="italic text-gold">our desk.</em>
            </RevealHeading>
          </div>
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 link-gold text-[0.72rem] tracking-caps uppercase font-medium text-ink"
            data-testid="insights-view-all"
          >
            All insights <ArrowUpRight size={13} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-10">
          {posts.map((p, i) => (
            <Link
              key={p.slug}
              to={`/insights/${p.slug}`}
              className="group block bg-bone"
              style={{ animationDelay: `${i * 90}ms` }}
              data-testid={`insight-card-${p.slug}`}
            >
              <div className="aspect-[3/4] overflow-hidden bg-sand">
                <img
                  src={p.hero_image}
                  srcSet={variantSrcSet(p.hero_image)}
                  sizes="(max-width: 768px) 100vw, 33vw"
                  width={1200}
                  height={800}
                  alt={p.title}
                  loading="lazy"
                  decoding="async"
                  onError={(e) => dropVariants(e.currentTarget)}
                  className="w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.04]"
                />
              </div>
              <div className="pt-6 pb-2">
                <div className="overline overline-muted">{p.read_minutes} min read</div>
                <h3 className="font-display text-[1.65rem] leading-[1.15] mt-3 tracking-tighter text-ink">
                  {p.title}
                </h3>
                <div className="relative mt-4 h-px bg-sand-dark overflow-hidden">
                  <div className="absolute inset-y-0 left-0 w-0 bg-gold transition-[width] duration-700 ease-out group-hover:w-full" />
                </div>
                <p className="text-[0.92rem] text-ink/65 mt-4 leading-relaxed font-light line-clamp-3">
                  {p.excerpt}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ============ EXPLORE ============ */}
      <section
        className="px-6 md:px-12 lg:px-20 py-16 bg-bone border-t border-sand-dark"
        data-testid="home-explore-section"
      >
        <ExploreLinks />
      </section>
    </div>
  );
}
